import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedCustomer } from '../../store/slices/customerSlice';
import AiAgentService from '../../api/AiAgentService';
import useFormatDate from '../../hooks/useFormatDate';
import './style.css';

const CustomersList = ({ onCustomerSelect, activeCustomer }) => {
    const dispatch = useDispatch();
    const selectedCustomer = useSelector(state => state.customer.selectedCustomer);
    const { formatDate } = useFormatDate();
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [searchName, setSearchName] = useState('');
    const [searchUrl, setSearchUrl] = useState('');
    const [skip, setSkip] = useState(0);
    const [total, setTotal] = useState(0);
    const [error, setError] = useState(null);
    const listRef = useRef(null);
    const observerRef = useRef(null);
    const loadingRef = useRef(false);
    const LIMIT = 10;

    // Синхронізуємо список користувачів з Redux при зміні ролі
    useEffect(() => {
        if (selectedCustomer) {
            setCustomers(prevCustomers => 
                prevCustomers.map(customer => 
                    customer.id === selectedCustomer.id 
                        ? { ...customer, role: selectedCustomer.role }
                        : customer
                )
            );
        }
    }, [selectedCustomer?.role?.title]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchCustomers = useCallback(async (reset = false) => {
        if (loadingRef.current) return;
        
        loadingRef.current = true;
        setLoading(true);
        setError(null);
        try {
            const currentSkip = reset ? 0 : skip;
            const response = await AiAgentService.getAllCustomers({
                customerName: searchName,
                serverUrl: searchUrl,
                limit: LIMIT,
                skip: currentSkip
            });

            const { customers: newCustomers, total: totalCount } = response;

            if (reset) {
                setCustomers(newCustomers);
                setSkip(LIMIT);
            } else {
                setCustomers(prev => [...prev, ...newCustomers]);
                setSkip(prev => prev + LIMIT);
            }

            setTotal(totalCount);
            setHasMore(newCustomers.length === LIMIT && (reset ? LIMIT : skip + LIMIT) < totalCount);
        } catch (error) {
            console.error('Error fetching customers:', error);
            setError(error.message || 'Помилка завантаження користувачів');
            setHasMore(false); // Зупиняємо подальші запити
        } finally {
            loadingRef.current = false;
            setLoading(false);
        }
    }, [skip, searchName, searchUrl]);

    // Ефект для початкового завантаження та пошуку
    useEffect(() => {
        setSkip(0);
        setCustomers([]);
        const timer = setTimeout(() => {
            fetchCustomers(true);
        }, 300); // debounce для пошуку
        
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchName, searchUrl]);

    // Infinite scroll
    useEffect(() => {
        const options = {
            root: listRef.current,
            rootMargin: '20px',
            threshold: 0.1
        };

        const handleObserver = (entries) => {
            const target = entries[0];
            if (target.isIntersecting && hasMore && !loading) {
                fetchCustomers();
            }
        };

        observerRef.current = new IntersectionObserver(handleObserver, options);
        
        const loadMoreTrigger = document.getElementById('load-more-trigger');
        if (loadMoreTrigger) {
            observerRef.current.observe(loadMoreTrigger);
        }

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [hasMore, loading, fetchCustomers]);

    const textForIcon = (customer) => {
        const name = customer.customer_name || "U";
        const words = name.split(' ');
        if (words.length > 1) {
            return words[0][0].toUpperCase() + words[1][0].toUpperCase();
        }
        return name.slice(0, 2).toUpperCase();
    };

    return (
        <div className="customers-sidebar">
            <div className="customers-header">
                <h2>Користувачі</h2>
                <div className="customers-count">
                    {total} {total === 1 ? 'користувач' : total < 5 ? 'користувачі' : 'користувачів'}
                </div>
            </div>

            <div className="customers-search">
                <input
                    type="text"
                    placeholder="Пошук по імені..."
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                    className="search-input"
                />
                <input
                    type="text"
                    placeholder="Пошук по URL..."
                    value={searchUrl}
                    onChange={(e) => setSearchUrl(e.target.value)}
                    className="search-input"
                />
            </div>

            <div className="customers-list" ref={listRef}>
                {customers.map((customer) => (
                    <div
                        key={customer.id}
                        className={`customer-item ${activeCustomer?.id === customer.id ? 'active' : ''}`}
                        onClick={() => {
                            dispatch(setSelectedCustomer(customer));
                            onCustomerSelect(customer);
                        }}
                    >
                        <div className="customer-icon">{textForIcon(customer)}</div>
                        <div className="customer-info">
                            <div className="customer-name-row">
                                <p className="customer-name">{customer.customer_name} <span className="customer-role">({customer.role?.title || 'N/A'})</span></p>
                                {customer.chats && customer.chats.length > 0 && (
                                    <span 
                                        className="chats-badge" 
                                        title={`Кількість чатів: ${customer.chats.length}`}
                                    >
                                        {customer.chats.length}
                                    </span>
                                )}
                            </div>
                            <div className='d-flex justify-content-between'>
                                <p className="customer-url">{customer.server_url}</p>
                                <p className="customer-date">{formatDate(customer.created_at)}</p>
                            </div>
                        </div>
                    </div>
                ))}

                {loading && (
                    <div className="loading-more">
                        <div className="spinner-small"></div>
                        <span>Завантаження...</span>
                    </div>
                )}

                {hasMore && !loading && <div id="load-more-trigger" style={{ height: '1px' }}></div>}

                {!loading && customers.length === 0 && (
                    <div className="no-customers">
                        {error ? (
                            <>
                                <p className="error-message">⚠️ {error}</p>
                                <button 
                                    className="retry-button"
                                    onClick={() => fetchCustomers(true)}
                                >
                                    Спробувати знову
                                </button>
                            </>
                        ) : (
                            <p>Користувачів не знайдено</p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CustomersList;
